import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  MdAccountTree,
  MdArticle,
  MdAssessment,
  MdBolt,
  MdBusiness,
  MdCategory,
  MdCheckCircle,
  MdClose,
  MdDashboard,
  MdDescription,
  MdExpandMore,
  MdListAlt,
  MdGridOn,
  MdGroup,
  MdHandshake,
  MdLaptopMac,
  MdManageAccounts,
  MdMenu,
  MdMoreHoriz,
  MdMoreTime,
  MdNightsStay,
  MdNotifications,
  MdNotificationsActive,
  MdPeople,
  MdPerson,
  MdSearch,
  MdSecurity,
  MdSettings,
  MdThumbUp,
  MdTune,
  MdViewList,
  MdWbSunny,
  MdWorkOutline,
} from "react-icons/md";
import SearchCommandPalette from "../search/SearchCommandPalette";
import { PriorityProblemBadge, normalizeProblemBadges } from "../../shared/PriorityProblemBadge";
import "./tailadmin-layout.css";

/** Active l’entrée courante (pages référentiel via clés `referentiels-<onglet>`). */
function navEntryIsActive(page, referentielsTab, itemKey) {
  if (!itemKey || itemKey.startsWith("_")) return false;
  if (itemKey.startsWith("referentiels-")) {
    const suffix = itemKey.slice("referentiels-".length);
    return page === "referentiels" && referentielsTab === suffix;
  }
  return page === itemKey;
}

/** Groupe (clé) du menu latéral contenant l’entrée active, ou `null` (ex. Dashboard seul). */
function resolveActiveNavGroupKey(navGroups, page, referentielsTab) {
  for (const group of navGroups) {
    for (const item of group.items || []) {
      if (item.heading || item.disabled || !item.key) continue;
      if (navEntryIsActive(page, referentielsTab, item.key)) return group.key;
    }
  }
  return null;
}

/** Taille des icônes dans le menu latéral. */
const NAV_ICON_SZ = 16;

/** Menu complet + icônes (groupes et entrées). */
const NAV_GROUPS_COMPLET = [
  {
    key: "registre",
    label: "Registre des traitements",
    Icon: MdListAlt,
    items: [
      { key: "ropa", label: "ROPA — Registre art. 30", Icon: MdListAlt },
      { key: "ropa-flux", label: "Carte des flux", Icon: MdAccountTree },
      { key: "consentement", label: "Consentements", Icon: MdThumbUp },
      { key: "preuves", label: "Preuves RGPD", Icon: MdDescription },
    ],
  },
  {
    key: "securite",
    label: "Sécurité des données",
    Icon: MdSecurity,
    items: [
      { key: "pssi", label: "PSSI — Politique sécurité", Icon: MdSecurity },
      { key: "inventaire-si", label: "Inventaire SI", Icon: MdLaptopMac },
      { key: "pra", label: "PRA — Plan reprise", Icon: MdBolt },
      { key: "incidents", label: "Incidents & violations", Icon: MdNotificationsActive },
    ],
  },
  {
    key: "risques",
    label: "Analyse des risques",
    Icon: MdAssessment,
    items: [
      { disabled: true, label: "AIPD / DPIA", badge: "À venir", Icon: MdMoreTime },
      { disabled: true, label: "Politique de conservation", badge: "À venir", Icon: MdMoreTime },
      { disabled: true, label: "AI Act — Conformité IA", badge: "À venir", Icon: MdMoreTime },
    ],
  },
  {
    key: "gouvernance",
    label: "Gouvernance RGPD",
    Icon: MdAccountTree,
    items: [
      { key: "droits", label: "Droits des personnes", Icon: MdPerson },
      { key: "matrice-raci", label: "Matrice RACI", Icon: MdGridOn },
      { key: "registre-sous-traitants", label: "Registre sous-traitants", Icon: MdHandshake },
    ],
  },
  {
    key: "dossier",
    label: "Dossier de conformité",
    Icon: MdDescription,
    items: [{ key: "reports-list", label: "Liste des rapports", Icon: MdDescription }],
  },
  {
    key: "configuration",
    label: "Configuration",
    Icon: MdSettings,
    items: [
      { heading: true, label: "— Référentiels —" },
      { key: "referentiels-personnes", label: "Responsables", Icon: MdPeople },
      { key: "referentiels-categories", label: "Catégories", Icon: MdCategory },
      { key: "referentiels-activites", label: "Activités", Icon: MdWorkOutline },
      { key: "referentiels-doctypes", label: "Types docs RGPD", Icon: MdArticle },
      { heading: true, label: "— Paramètres —" },
      { key: "societe-config", label: "Informations société", Icon: MdBusiness },
      { key: "team-config", label: "Equipe & invitations", Icon: MdGroup },
      { key: "dashboard-config", label: "Config. scores tableau de bord", Icon: MdTune },
      { key: "reports-config", label: "Config. rapports", Icon: MdManageAccounts },
    ],
  },
];

const NAV_GROUPS_EXPRESS = [
  {
    key: "registre",
    label: "Registre des traitements",
    Icon: MdListAlt,
    items: [
      { key: "ropa", label: "ROPA — Registre art. 30", Icon: MdListAlt },
      { key: "ropa-flux", label: "Carte des flux", Icon: MdAccountTree },
      { key: "consentement", label: "Consentements", Icon: MdThumbUp },
    ],
  },
  {
    key: "express_more",
    label: "Autres",
    Icon: MdMoreHoriz,
    items: [
      { key: "droits", label: "Droits des personnes", Icon: MdPerson },
      { key: "societe-config", label: "Informations société", Icon: MdBusiness },
    ],
  },
];

function displayName(user) {
  if (!user?.email) return "Utilisateur";
  const dn = (user.display_name || "").trim();
  if (dn) return dn;
  const beforeAt = user.email.split("@")[0];
  return beforeAt ? beforeAt.charAt(0).toUpperCase() + beforeAt.slice(1) : user.email;
}

function formatRoleLine(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return "";
  return roles.map((r) => r.label || r.code).join(" · ");
}

/** Libellé menu avec icône optionnelle (alignement cohérent). */
function NavMenuLabel({ Icon: I, label }) {
  return (
    <span className="ta-menu-item-inner">
      {I ? <I size={NAV_ICON_SZ} aria-hidden className="ta-menu-icon" /> : null}
      <span className="ta-menu-item-text">{label}</span>
    </span>
  );
}

export default function AppNav({
  mode,
  page,
  onNavigate,
  onLogout,
  currentUser,
  criticalActions = [],
  searchData = {},
  sessionTenant = null,
  organizations = [],
  tenantRoles = [],
  onSwitchOrganization,
  onCreateOrganization,
  onRequestDeleteOrganization,
  orgMenuBusy = false,
  onOpenAccount,
  sessionReadOnly = false,
  referentielsTab = "personnes",
}) {
  const navGroups = useMemo(() => (mode === "express" ? NAV_GROUPS_EXPRESS : NAV_GROUPS_COMPLET), [mode]);

  const activeNavGroupKey = useMemo(
    () => resolveActiveNavGroupKey(navGroups, page, referentielsTab),
    [navGroups, page, referentielsTab]
  );

  const [openSections, setOpenSections] = useState(() => new Set());

  useLayoutEffect(() => {
    setOpenSections(new Set(navGroups.map((group) => group.key)));
  }, [navGroups]);

  useLayoutEffect(() => {
    if (!activeNavGroupKey) return;
    setOpenSections((prev) => {
      if (prev.has(activeNavGroupKey)) return prev;
      const next = new Set(prev);
      next.add(activeNavGroupKey);
      return next;
    });
  }, [activeNavGroupKey]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgAppMode, setNewOrgAppMode] = useState("complet");
  const [orgListOpen, setOrgListOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const notifRef = useRef(null);
  const orgRef = useRef(null);
  const userMenuRef = useRef(null);
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearchSelect = useCallback(
    (targetPage, context) => {
      onNavigate(targetPage, context);
      setSearchOpen(false);
      if (isMobile) setMobileMenuOpen(false);
    },
    [onNavigate, isMobile]
  );

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  const toggleNavGroup = (groupKey) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const navigateTo = (itemKey) => {
    if (!itemKey) return;
    onNavigate(itemKey);
    if (isMobile) setMobileMenuOpen(false);
  };

  useEffect(() => {
    const savedDark = localStorage.getItem("rgpd_dark_mode");
    const savedSidebar = localStorage.getItem("rgpd_sidebar_collapsed");
    setDarkMode(savedDark === "1");
    setSidebarCollapsed(savedSidebar === "1");

    const updateViewport = () => setIsMobile(window.innerWidth < 1024);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("ta-dark", darkMode);
    localStorage.setItem("rgpd_dark_mode", darkMode ? "1" : "0");
  }, [darkMode]);

  useEffect(() => {
    document.body.classList.toggle("ta-sidebar-collapsed", !isMobile && sidebarCollapsed);
    localStorage.setItem("rgpd_sidebar_collapsed", sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed, isMobile]);

  useEffect(() => {
    document.body.classList.toggle("ta-mobile-nav-open", isMobile && mobileMenuOpen);
    document.documentElement.classList.toggle("ta-mobile-nav-open", isMobile && mobileMenuOpen);

    return () => {
      document.body.classList.remove("ta-mobile-nav-open");
      document.documentElement.classList.remove("ta-mobile-nav-open");
    };
  }, [isMobile, mobileMenuOpen]);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    } else {
      setSidebarHovered(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleToggleMenu = () => {
    if (isMobile) {
      setMobileMenuOpen((v) => !v);
      return;
    }

    setSidebarHovered(false);
    setSidebarCollapsed((v) => !v);
  };

  const sidebarCompact = !isMobile && sidebarCollapsed && !sidebarHovered;
  const sidebarHoverExpand = !isMobile && sidebarCollapsed && sidebarHovered;

  return (
    <>
      <aside
        className={[
          "ta-sidebar",
          isMobile && mobileMenuOpen ? "ta-mobile-open" : "",
          sidebarCompact ? "ta-sidebar-compact" : "",
          sidebarHoverExpand ? "ta-sidebar-hover-expand" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onMouseEnter={() => {
          if (!isMobile && sidebarCollapsed) setSidebarHovered(true);
        }}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="ta-sidebar-header">
          <span className="ta-sidebar-brand-full">Hapivot</span>
          <span className="ta-sidebar-brand-mark" aria-hidden>
            H
          </span>
          {isMobile && (
            <button
              className="ta-drawer-close"
              aria-label="Fermer le menu"
              onClick={() => setMobileMenuOpen(false)}
            >
              <MdClose size={16} />
            </button>
          )}
        </div>
        <div className="ta-sidebar-section">
          <button
            type="button"
            className={`ta-menu-item ${page === "dashboard" ? "active" : ""}`}
            title="Dashboard"
            onClick={() => navigateTo("dashboard")}
          >
            <NavMenuLabel Icon={MdDashboard} label="Dashboard" />
          </button>
          {navGroups.map((group) => {
            const childActive = group.items.some(
              (it) =>
                it.key &&
                !it.heading &&
                !it.disabled &&
                navEntryIsActive(page, referentielsTab, it.key)
            );
            const expanded = openSections.has(group.key);
            const GroupIcon = group.Icon;
            return (
              <div key={group.key}>
                <button
                  type="button"
                  className={`ta-menu-item ta-menu-item--group ${childActive ? "active" : ""}`}
                  title={group.label}
                  onClick={() => toggleNavGroup(group.key)}
                  aria-expanded={expanded}
                >
                  <NavMenuLabel Icon={GroupIcon} label={group.label} />
                  <MdExpandMore
                    size={16}
                    aria-hidden
                    className={`ta-menu-chevron${expanded ? " is-open" : ""}`}
                  />
                </button>
                {expanded ? (
                  <div className="ta-submenu">
                    {group.items.map((item, idx) => {
                      if (item.heading) {
                        return (
                          <div key={`h-${group.key}-${idx}`} className="ta-submenu-heading">
                            {item.label}
                          </div>
                        );
                      }
                      if (item.disabled) {
                        const DisIcon = item.Icon;
                        return (
                          <div
                            key={`d-${group.key}-${idx}`}
                            className="ta-menu-item ta-menu-item--disabled"
                            aria-disabled="true"
                          >
                            <span className="ta-menu-item-inner ta-menu-item-inner--fill">
                              {DisIcon ? (
                                <DisIcon size={NAV_ICON_SZ} aria-hidden className="ta-menu-icon" />
                              ) : null}
                              <span className="ta-menu-item-text">{item.label}</span>
                            </span>
                            {item.badge ? <span className="ta-menu-badge">{item.badge}</span> : null}
                          </div>
                        );
                      }
                      const ItemIcon = item.Icon;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          className={`ta-menu-item ${navEntryIsActive(page, referentielsTab, item.key) ? "active" : ""}`}
                          title={item.label}
                          onClick={() => navigateTo(item.key)}
                        >
                          <NavMenuLabel Icon={ItemIcon} label={item.label} />
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </aside>
      {isMobile && mobileMenuOpen && <div className="ta-mobile-backdrop" onClick={() => setMobileMenuOpen(false)} />}

      <header className="ta-header">
        <div className="ta-header-left">
          <button
            className="ta-icon-btn"
            aria-label="Toggle sidebar"
            onClick={handleToggleMenu}
          >
            <MdMenu size={18} />
          </button>
          <div
            className="ta-search-wrap"
            onClick={() => setSearchOpen(true)}
            onKeyDown={(e) => e.key === "Enter" && setSearchOpen(true)}
            role="button"
            tabIndex={0}
            aria-label="Recherche RGPD (⌘K)"
          >
            <span className="ta-search-icon"><MdSearch size={14} /></span>
            <input
              className="ta-search-input"
              placeholder="Rechercher pages, traitements, personnes..."
              readOnly
            />
            <span className="ta-search-kbd">⌘ K</span>
          </div>
          <SearchCommandPalette
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            onSelect={handleSearchSelect}
            searchData={searchData}
            mode={mode}
          />
        </div>
        <div className="ta-header-right">
          <button
            className="ta-icon-btn ta-theme-btn"
            aria-label="Toggle theme"
            onClick={() => setDarkMode((v) => !v)}
            title={darkMode ? "Passer en mode clair" : "Passer en mode sombre"}
          >
            {darkMode ? <MdWbSunny size={16} /> : <MdNightsStay size={16} />}
          </button>
          <div className="ta-notif-wrap" ref={notifRef}>
            <button
              className={`ta-icon-btn ${notifOpen ? "active" : ""}`}
              aria-label="Notifications"
              aria-expanded={notifOpen}
              onClick={(e) => { e.stopPropagation(); setNotifOpen(v => !v); }}
              title="Actions prioritaires"
            >
              <MdNotifications size={16} />
              {criticalActions.length > 0 && (
                <span className="ta-notif-badge">{criticalActions.length}</span>
              )}
            </button>
            {notifOpen && (
              <div className="ta-notif-dropdown">
                <div className="ta-notif-title">Actions prioritaires</div>
                {criticalActions.length === 0 ? (
                  <div className="ta-notif-empty" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><MdCheckCircle size={14} /> Aucune action prioritaire en suspens</div>
                ) : (
                  criticalActions.map((a, i) => (
                    <button
                      key={i}
                      className="ta-notif-item"
                      onClick={() => { onNavigate(a.page, a.navContext); setNotifOpen(false); if (isMobile) setMobileMenuOpen(false); }}
                    >
                      <span className={`ta-notif-urgence ${a.badgeTone || (a.urgence === "Critique" ? "critique" : "haute")}`}>{a.badgeText || a.urgence}</span>
                      <div className="ta-notif-item-main">
                        <div className="ta-notif-item-tags">
                          {normalizeProblemBadges(a).map((b, bi) => (
                            <PriorityProblemBadge key={bi} variant={b.variant} text={b.text} />
                          ))}
                          {a.module && <span className="ta-notif-module">{a.module}</span>}
                        </div>
                        <span className="ta-notif-label">{a.label}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {onSwitchOrganization && onCreateOrganization && (
            <div className="ta-org-wrap" ref={orgRef}>
              <button
                type="button"
                className="ta-org-trigger"
                aria-expanded={orgOpen}
                aria-haspopup="listbox"
                disabled={orgMenuBusy || sessionReadOnly}
                title={sessionReadOnly ? "Consultation lecture seule — changement d’organisation désactivé" : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (sessionReadOnly) return;
                  setOrgOpen((v) => !v);
                }}
              >
                <span className="ta-org-icon" aria-hidden>
                  <MdBusiness size={16} />
                </span>
                <span className="ta-org-text">
                  <span className="ta-org-name">{sessionTenant?.name || "Organisation"}</span>
                  {formatRoleLine(tenantRoles) ? (
                    <span className="ta-org-roles">{formatRoleLine(tenantRoles)}</span>
                  ) : null}
                </span>
                <span className="ta-org-arrow"><MdExpandMore size={14} /></span>
              </button>
              {orgOpen && (
                <div className="ta-org-dropdown" role="listbox">
                  <div className="ta-org-dropdown-title-row">
                    <span className="ta-org-dropdown-title">Organisations</span>
                    <button
                      type="button"
                      className="ta-org-dropdown-list-icon"
                      aria-label="Ouvrir la liste détaillée des organisations"
                      title="Liste détaillée (basculer, supprimer…)"
                      disabled={orgMenuBusy || sessionReadOnly}
                      onClick={(e) => {
                        if (sessionReadOnly) return;
                        e.stopPropagation();
                        setOrgListOpen(true);
                        setOrgOpen(false);
                      }}
                    >
                      <MdViewList size={16} />
                    </button>
                  </div>
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      type="button"
                      role="option"
                      aria-selected={org.id === sessionTenant?.id}
                      className={`ta-org-item ${org.id === sessionTenant?.id ? "active" : ""}`}
                      disabled={orgMenuBusy || sessionReadOnly}
                      onClick={() => {
                        if (sessionReadOnly) return;
                        if (org.id !== sessionTenant?.id) onSwitchOrganization(org.id);
                        setOrgOpen(false);
                      }}
                    >
                      <span className="ta-org-item-name">{org.name}</span>
                      {formatRoleLine(org.roles) ? (
                        <span className="ta-org-item-roles">{formatRoleLine(org.roles)}</span>
                      ) : null}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="ta-org-add"
                    disabled={orgMenuBusy || sessionReadOnly}
                    onClick={() => {
                      if (sessionReadOnly) return;
                      setCreateOrgOpen(true);
                      setOrgOpen(false);
                    }}
                  >
                    + Nouvelle organisation
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="ta-user-chip-wrap" ref={userMenuRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="ta-user-chip"
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                e.stopPropagation();
                if (sessionReadOnly || !onOpenAccount) return;
                setUserMenuOpen((v) => !v);
              }}
              style={{
                cursor: onOpenAccount && !sessionReadOnly ? "pointer" : "default",
                background: "#fff",
                font: "inherit",
              }}
            >
              <div className="ta-user-avatar">{(displayName(currentUser) || "U")[0].toUpperCase()}</div>
              <div className="ta-user-name">{displayName(currentUser)}</div>
              {onOpenAccount && !sessionReadOnly ? <span className="ta-user-arrow"><MdExpandMore size={12} /></span> : null}
            </button>
            {userMenuOpen && onOpenAccount && !sessionReadOnly ? (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  marginTop: 8,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                  minWidth: 200,
                  zIndex: 1000,
                  padding: 6,
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onOpenAccount();
                    setUserMenuOpen(false);
                    if (isMobile) setMobileMenuOpen(false);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 12px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#101828",
                  }}
                >
                  Mon compte
                </button>
              </div>
            ) : null}
          </div>
          <button className="ta-header-btn" onClick={onLogout} title="Déconnexion">
            Déconnexion
          </button>
        </div>
      </header>
      {orgListOpen && onSwitchOrganization && onCreateOrganization && (
        <div
          className="ta-org-modal-backdrop"
          role="presentation"
          onClick={() => setOrgListOpen(false)}
        >
          <div
            className="ta-org-modal ta-org-list-modal"
            role="dialog"
            aria-labelledby="ta-org-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div id="ta-org-list-title" className="ta-org-modal-title">
              Mes organisations
            </div>
            <p className="ta-org-modal-hint">
              Basculez vers une organisation ou, si vous en êtes le créateur, demandez sa suppression (confirmation par e-mail).
            </p>
            <div className="ta-org-list-table">
              {organizations.map((org) => (
                <div key={org.id} className="ta-org-list-row">
                  <div className="ta-org-list-main">
                    <div className="ta-org-list-name">{org.name}</div>
                    {formatRoleLine(org.roles) ? (
                      <div className="ta-org-list-roles">{formatRoleLine(org.roles)}</div>
                    ) : null}
                  </div>
                  <div className="ta-org-list-actions">
                    <button
                      type="button"
                      className="ta-org-list-switch"
                      disabled={orgMenuBusy || org.id === sessionTenant?.id || sessionReadOnly}
                      onClick={() => {
                        if (sessionReadOnly) return;
                        if (org.id !== sessionTenant?.id) onSwitchOrganization(org.id);
                        setOrgListOpen(false);
                      }}
                    >
                      {org.id === sessionTenant?.id ? "Active" : "Basculer"}
                    </button>
                    {org.is_creator && onRequestDeleteOrganization ? (
                      <button
                        type="button"
                        className="ta-org-list-delete"
                        disabled={orgMenuBusy || sessionReadOnly}
                        title={sessionReadOnly ? "Lecture seule" : "Suppression après confirmation par e-mail"}
                        onClick={() => {
                          if (sessionReadOnly) return;
                          const ok = window.confirm(
                            `Un e-mail de confirmation sera envoyé à ${currentUser?.email || "votre adresse"}. Après validation du lien, l’organisation sera définitivement retirée de votre espace. Continuer ?`
                          );
                          if (!ok) return;
                          onRequestDeleteOrganization(org.id);
                          setOrgListOpen(false);
                        }}
                      >
                        Supprimer
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <div className="ta-org-modal-actions">
              <button
                type="button"
                className="ta-org-modal-submit"
                disabled={orgMenuBusy || sessionReadOnly}
                onClick={() => {
                  if (sessionReadOnly) return;
                  setCreateOrgOpen(true);
                  setOrgListOpen(false);
                }}
              >
                + Nouvelle organisation
              </button>
              <button type="button" className="ta-org-modal-cancel" onClick={() => setOrgListOpen(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {createOrgOpen && onCreateOrganization && !sessionReadOnly && (
        <div
          className="ta-org-modal-backdrop"
          role="presentation"
          onClick={() => {
            setCreateOrgOpen(false);
            setNewOrgName("");
          }}
        >
          <div
            className="ta-org-modal"
            role="dialog"
            aria-labelledby="ta-org-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div id="ta-org-modal-title" className="ta-org-modal-title">
              Nouvelle organisation
            </div>
            <p className="ta-org-modal-hint">
              Un espace vide sera créé : vous pourrez renseigner les informations société et les données plus tard.
            </p>
            <label className="ta-org-modal-label" htmlFor="ta-org-name">
              Nom
            </label>
            <input
              id="ta-org-name"
              className="ta-org-modal-input"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Ex. Ma holding"
              autoComplete="organization"
            />
            <label className="ta-org-modal-label" htmlFor="ta-org-mode">
              Mode produit
            </label>
            <select
              id="ta-org-mode"
              className="ta-org-modal-select"
              value={newOrgAppMode}
              onChange={(e) => setNewOrgAppMode(e.target.value)}
            >
              <option value="complet">Complet</option>
              <option value="light">Express (audit léger)</option>
            </select>
            <div className="ta-org-modal-actions">
              <button
                type="button"
                className="ta-org-modal-cancel"
                onClick={() => {
                  setCreateOrgOpen(false);
                  setNewOrgName("");
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                className="ta-org-modal-submit"
                disabled={orgMenuBusy || !newOrgName.trim()}
                onClick={async () => {
                  const name = newOrgName.trim();
                  if (!name) return;
                  await onCreateOrganization(name, newOrgAppMode);
                  setCreateOrgOpen(false);
                  setNewOrgName("");
                  setNewOrgAppMode("complet");
                }}
              >
                Créer et basculer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
